


// components/Tax/InstallationandremovalSpecialfeeui.tsx
import * as React from "react";
import { DollarSign } from "lucide-react";
import { showError } from "toast/toast";
import { handleApiError } from "utils/errorHandler";
import { getAuthCookie } from "../../utils/authCookies";
import { readResolveTaxable } from "orderResolve/utils/readResolveTaxable";
import {
  fetchInstallationSpecialFeesByMembership,
  installationOrderUrls,
} from "api/installationApi";
import { forcePermissionsFalse, resolveCheckoutFeeApiIds, unwrapListResponse } from "api/salesOrderApi";

export type SpecialFee = {
  id: string;
  name: string;
  amount: number;
  taxable?: boolean;
  tax_percentage?: number | null;
  tax_amount?: number;  // ✅ Tax amount calculated by parent tax engine
  status?: boolean;
  tax_rule_id?: string | null;
  tax_group_id?: string | null;
  required?: boolean;  // ✅ If true, fee must be selected by default
};

type Props = {
  token: string;
  personId: string;
  formik?: any;                         // Formik instance (optional, use value prop instead)
  name?: string;                        // defaults to "specialFees"
  value?: SpecialFee[];                 // ✅ Direct value prop (preferred over Formik)
  apiBase?: string;                     // defaults to env
  readOnly?: boolean;                   // view mode -> true
  disabled?: boolean;                   // ✅ Disable all checkboxes when recompute is running
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  supplierId: string;               // seller org id fallback (signage: prefer auth cookie organizationId)
  /** Optional order context for view/edit — buyer membership + seller org on the order. */
  order?: {
    seller_org_id?: string;
    signage_id?: string;
    agent_id?: string;
    team_member_id?: string;
    buyer_membership_id?: string;
    agent_membership_id?: string;
    team_member_membership_id?: string;
    source_membership_id?: string;
    customer_membership_id?: string;
    inventory_source?: string;
  } | null;
  onUserChange?: (nextFees: SpecialFee[]) => void;  // fires after user toggles
  onOptionsLoaded?: (allOptions: any[]) => void;    // fires when all available fees are loaded
  /** When false, required catalog fees are not auto-checked (add-removal: do not copy installation-job fees). Default true. */
  autoSelectRequired?: boolean;
  /** Catalog loads from vendor org + end-user membership. */
  orderId?: string;
  orderType?: "installation" | "removal";
};

export default function SpecialFeeui({
  token,
  personId,
  formik,
  name = "specialfee",
  value,  // ✅ Direct value prop (preferred over Formik)
  apiBase = (process.env.REACT_APP_API_URL || "") as string,
  readOnly,
  disabled = false,  // ✅ Default to false
  className,
  supplierId,
  order,
  style,
  title = "Special Fees",
  onUserChange,
  onOptionsLoaded,
  autoSelectRequired = true,
}: Props) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [options, setOptions] = React.useState<SpecialFee[]>([]);
  // add this tiny helper near the top of the file
  function deepGet(obj: any, path: string, fallback: any) {
    return path.split('.').reduce((o, k) => (o && k in o ? o[k] : undefined), obj) ?? fallback;
  }

  // ... inside the component:
  // ✅ Use value prop if provided, otherwise read from Formik
  const getSelected = () => {
    // Prefer direct value prop over Formik
    if (value !== undefined) {
      return Array.isArray(value) ? value : [];
    }
    // Fall back to Formik if value prop not provided
    if (formik) {
      const current = deepGet(formik?.values, name, []);
      return Array.isArray(current) ? current : [];
    }
    return [];
  };

  // ✅ Use useMemo to recompute when value prop or Formik changes
  const selected: SpecialFee[] = React.useMemo(() => {
    return getSelected();
  }, [value, formik?.values, name]);

  // Catalog: GET /special-fees/organizations/{vendor_org}/by-membership/{end_user_membership}
  React.useEffect(() => {
    let isMounted = true;
    const { organizationId: feeOrganizationId, membershipId: feeMembershipId } =
      resolveCheckoutFeeApiIds(supplierId, personId, order);
    if (!feeOrganizationId || !feeMembershipId) {
      setOptions([]);
      setError(null);
      setLoading(false);
      return;
    }
    (async () => {
      const userId = getAuthCookie("membershipId");
      const licenseType = getAuthCookie("licenseType");
      const userName = getAuthCookie("userName");
      const API = apiBase.replace(/\/$/, "");
      const catalogPath = installationOrderUrls.specialFeesByOrganizationMembership(
        feeOrganizationId,
        feeMembershipId
      );
      const url = `${API}${forcePermissionsFalse(catalogPath)}`;
      
      try {
        setLoading(true);
        setError(null);
        const res = await fetchInstallationSpecialFeesByMembership(
          token,
          feeOrganizationId,
          feeMembershipId
        );
        if (!res.ok) {
          if (!isMounted) return;
          setOptions([]);
          setError(`Failed to load special fees (${res.status})`);
          await handleApiError(new Error(`HTTP ${res.status}`), {
            module: "other",
            action: "InstallationandremovalSpecialfeeui_fetch_special_fees",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url,
            method: "GET",
            additionalInfo: {
              supplierId,
              personId,
              organizationId: feeOrganizationId,
              membershipId: feeMembershipId,
              status: res.status,
            },
          }, token);
          return;
        }
        const raw = await res.json().catch(() => []);
        const arr = unwrapListResponse(raw);
        const filtered = arr.filter((f: any) => f?.status !== false);

        const norm: SpecialFee[] = filtered.map((f: any) => ({
          id: String(f.id ?? f._id ?? f.value ?? f.key ?? ""),
          name: String(f.name ?? f.label ?? "Special Fee"),
          amount: Number(f.amount ?? f.price ?? 0),
          taxable: readResolveTaxable(f, ["taxable", "is_taxable", "specialfee_taxable"], true),
          tax_percentage: f.tax_percentage ?? null,
          status: f.status !== false,
          tax_group_id: f.tax_group_id ?? null,
          tax_rule_id: f.tax_rule_id ?? null,
          required: f.required === true,  // ✅ Capture required field
        }));

        // if (!isMounted) return;
        setOptions(norm);

        // ✅ Pass complete raw data to parent (including required field)
        onOptionsLoaded?.(filtered);
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.message || "Failed to load special fees");
        setOptions([]);
        await handleApiError(e, {
          module: "other",
          action: "InstallationandremovalSpecialfeeui_fetch_special_fees",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url,
          method: "GET",
          additionalInfo: {
            supplierId,
            personId,
            organizationId: feeOrganizationId,
            membershipId: feeMembershipId,
          },
        }, token);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [token, personId, apiBase, supplierId, order]);

  // ✅ Auto-select required fees in edit mode when options are loaded
  React.useEffect(() => {
    if (!autoSelectRequired || readOnly || !options.length) return; // Only in edit mode

    const requiredFees = options.filter(f => f.required === true);
    if (requiredFees.length === 0) return; // No required fees

    const currentSelectedIds = new Set(selected.map(s => s.id));
    // console.log(currentSelectedIds, "currentSelectedIds - special fees2");
    const requiredIds = new Set(requiredFees.map(f => f.id));
    // console.log(requiredIds, "requiredIds - special fees2");
    // Check if any required fees are missing
    const missingRequired = requiredFees.filter(f => !currentSelectedIds.has(f.id));
    // console.log(missingRequired, "missingRequired - special fees2");

    if (missingRequired.length > 0) {
      // Build complete fee objects for required fees
      const requiredFeeObjects = requiredFees.map(f => ({
        id: f.id,
        name: f.name,
        amount: f.amount,
        taxable: readResolveTaxable(f, ["taxable", "is_taxable", "specialfee_taxable"], true),
        tax_percentage: f.tax_percentage ?? null,
        tax_rule_id: f.tax_rule_id ?? null,
        tax_group_id: f.tax_group_id ?? null,
        required: true,
      }));

      // Merge with existing selected fees (avoid duplicates)
      const existingSelected = selected.filter(s => !requiredIds.has(s.id));
      const merged = [...existingSelected, ...requiredFeeObjects];

      // console.log("Auto-selecting required fees:", requiredFeeObjects);
      formik.setFieldValue(name, merged, false);
      onUserChange?.(merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length, readOnly, autoSelectRequired]); // Only run when options are loaded or readOnly changes

  // ✅ Enrich selected fees with complete data from options (only when needed)
  React.useEffect(() => {
    if (!selected?.length || !options?.length) return;

    const byId = new Map(options.map(o => [o.id, o]));
    const merged = selected.map(s => ({
      id: s.id,
      name: s.name ?? byId.get(s.id)?.name ?? "Special Fee",
      amount: Number(s.amount ?? byId.get(s.id)?.amount ?? 0),
        taxable: readResolveTaxable(
          { taxable: s.taxable, specialfee_taxable: (s as { specialfee_taxable?: boolean }).specialfee_taxable },
          ["taxable", "specialfee_taxable", "is_taxable"],
          readResolveTaxable(byId.get(s.id) as Record<string, unknown>, ["taxable", "is_taxable", "specialfee_taxable"], true)
        ),
      tax_percentage: s.tax_percentage ?? byId.get(s.id)?.tax_percentage ?? null,
      tax_rule_id: s.tax_rule_id ?? byId.get(s.id)?.tax_rule_id ?? null,
      tax_group_id: s.tax_group_id ?? byId.get(s.id)?.tax_group_id ?? null,
      tax_amount: s.tax_amount,  // ✅ Preserve tax_amount if parent already calculated it
      required: s.required ?? byId.get(s.id)?.required ?? false,  // ✅ Preserve required field
    }));

    // 🛡️ GUARD: Only update if there are missing fields to prevent infinite loop
    const needsEnrichment = merged.some((m, i) => {
      const s = selected[i];
      if (!s) return true; // new item
      // Check if any critical field is missing in selected but present in merged
      return (!s.tax_rule_id && m.tax_rule_id) ||
        (!s.tax_group_id && m.tax_group_id) ||
        (!s.name && m.name) ||
        (s.required !== m.required);
    });

    if (needsEnrichment) {
      // console.log(merged, "merged - enriching missing fields");
      formik.setFieldValue(name, merged, false);
    }
  }, [selected.length, options.length, JSON.stringify(selected.map(s => s.id))]); // eslint-disable-line

  const isSelected = React.useCallback(
    (id: string) => selected.some(f => f.id === id),
    [selected]
  );

  const toggle = (opt: SpecialFee) => {
    if (readOnly || disabled) return;  // ✅ Prevent toggling when disabled

    // ✅ Prevent deselecting required fees
    const isRequired = opt.required === true || selected.find(s => s.id === opt.id)?.required === true;
    const exists = isSelected(opt.id);

    if (exists && isRequired) {
      // Don't allow deselecting required fees
      return;
    }

    // ✅ Build complete fee object with all fields
    const next = exists
      ? selected.filter(f => f.id !== opt.id)
      : [...selected, {
        id: opt.id,
        name: opt.name,
        amount: opt.amount,
        taxable: readResolveTaxable(opt as Record<string, unknown>, ["taxable", "is_taxable", "specialfee_taxable"], true),
        tax_percentage: opt.tax_percentage ?? null,
        tax_rule_id: opt.tax_rule_id ?? null,
        tax_group_id: opt.tax_group_id ?? null,
        required: opt.required ?? false,
      }];

    // console.log("Toggle - selected fees:", next);

    // ✅ Update Formik (for form submission)
    formik.setFieldValue(name, next, false);

    // ✅ Notify parent immediately (parent uses state, not Formik!)
    onUserChange?.(next);
  };

  const money = (n: number) => `$${Number(n || 0).toFixed(2)}`;
  const pct = (n: number | null | undefined) => {
    if (n === null || n === undefined || !Number.isFinite(n)) return "0.00%";
    return `${Number(n).toFixed(2)}%`;
  };

  // Edit mode UI (with checkboxes)
  if (!readOnly) {
    return (
      <>
        {
          <style>
            {`
            .border-redius-top-0{
              border-radius: 0 0 15px 15px !important;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .special-fee-loader {
              display: inline-block;
              animation: spin 1s linear infinite;
              font-size: 18px;
              width: 18px;
              height: 18px;
              line-height: 18px;
              color: #2563eb;
            }
          `}
          </style>
        }
        <div className={className}>
          <div className="card shadow-border-1" style={style}>
            <div className="card-header bg-light">
              <h5 className="card-title d-flex align-items-center mb-0">
                <DollarSign className="ms-1" style={{ color: "#2563eb" }} />
                <span className="ms-2">Special Fees</span>
                {/* <small className="ms-auto text-muted">Qty fixed to 1</small> */}
                {disabled && (
                  <span className="ms-2 d-flex align-items-center" style={{ color: "#2563eb", fontWeight: 500 }}>
                    <i className="ri-loader-4-line special-fee-loader me-2" style={{ fontSize: "18px" }} />
                    <span style={{ fontSize: "13px" }}>Calculating...</span>
                  </span>
                )}
              </h5>
            </div>

            <div className="border-top-0 card-body border-redius-top-0">
              {loading && <div className="text-muted small mb-2">loading…</div>}
              {!!error && <div className="text-danger small mb-2">{error}</div>}
              {!loading && options.length === 0 && (
                <div className="">No Special Fee Selected</div>
              )}

              {options.map((f) => {
                const checked = isSelected(f.id);
                const isRequired = f.required === true || selected.find(s => s.id === f.id)?.required === true;
                const isDisabledCheckbox = (checked && isRequired) || disabled; // ✅ Disable if required+selected OR parent disabled

                return (
                  <div
                    key={f.id}
                    className={
                      "d-flex justify-content-between align-items-center p-3 mb-2 border rounded-4 " +
                      (checked ? "border-primary bg-primary-subtle" : "border-light hover-border-primary") +
                      (isDisabledCheckbox ? " opacity-75" : "")
                    }
                    style={{
                      userSelect: "none",
                      cursor: isDisabledCheckbox ? "not-allowed" : "pointer"
                    }}
                    onClick={() => !isDisabledCheckbox && toggle(f)}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={checked}
                        disabled={isDisabledCheckbox}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => !isDisabledCheckbox && toggle(f)}
                      />
                      <div className="fw-semibold">
                        {f.name}
                        {isRequired && <span className="ms-1 text-danger" title="Required">*</span>}
                      </div>
                    </div>
                    <div className="fw-semibold text-nowrap text-back">
                      {money(f.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }

  // View mode UI (read-only, matching image design)
  return (
    <div className={className}>
      {
        <style>
          {`
            .border-redius-top-0{
              border-radius: 0 0 15px 15px !important;
            }
          `}
        </style>
      }
      <div className="card shadow-border-1" style={style}>
        <div className="card-header bg-light">
          <h5 className="card-title d-flex align-items-center mb-0">
            <DollarSign className="ms-1" style={{ color: "#00a63e" }} />
            <span className="ms-2">Special Fee</span>
          </h5>
        </div>
        <div className="border-top-0 card-body border-redius-top-0">
          {loading && <div className="text-muted small mb-2">loading…</div>}
          {!!error && <div className="text-danger small mb-2">{error}</div>}
          {!loading && !error && selected.length === 0 && (
            <div className="">No Special Fee Selected</div>
          )}
          {selected.map((f) => {
            // In view mode, required comes from options (API) since saved order data may not include it
            const option = options.find((o) => o.id === f.id);
            const isRequired = f.required === true || option?.required === true;
            return (
            <div
              key={f.id}
              className="d-flex justify-content-between align-items-center p-3 mb-2 border br-8 bg-white"

            >

              <div className="flex-grow-1">
                <div className="fw-semibold">
                  {f.name}
                  {isRequired && <span className="ms-1 text-danger" title="Required">*</span>}
                </div>
                {/* {(f.tax_amount || (f.tax_percentage !== null && f.tax_percentage !== undefined)) && (
                  <div className="small text-muted mt-1">
                    Tax: <strong>{money(f.tax_amount || 0)}</strong>{" "}
                    <span className="text-secondary">({pct(f.tax_percentage)})</span>
                  </div>
                )} */}
              </div>
              <div className="fw-semibold text-nowrap ms-3">{money(f.amount)}</div>
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
}
